import {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {ProjectDataIndex} from '../api/types.ts';

/*
 * A tab's view: where a whiteboard is panned and zoomed to, where a scene's
 * camera is. The same view comes from two places:
 *
 *  - the address bar, so a link can open a tab looking at a particular spot
 *    (?tab=tab2&fullscreen=1&x=400&y=-120&zoom=1.5, or &cam=4,3,-8&target=0,1,0);
 *  - the render page's window.mixiplyRender.setView, which the renderer calls
 *    to take several pictures of one tab without reloading it.
 *
 * Each tab that has a view registers a TabViewController for its slot while it
 * is mounted (useTabView). Coordinates are the tab's own: canvas
 * units for a whiteboard, scene units for a scene.
 */
export interface TabView {
    // Whiteboard: the canvas point at the centre of the view, and the zoom (1 = 100%).
    x?: number;
    y?: number;
    zoom?: number;
    // Whiteboard: fit this canvas area [x, y, width, height] into the view.
    region?: [number, number, number, number];
    // Whiteboard and scene: fit everything in.
    fit?: boolean;
    // Scene: where the camera is, what it looks at, and its vertical field of view in degrees.
    camera?: [number, number, number];
    target?: [number, number, number];
    fov?: number;
    // Scene: orbit the target instead of placing the camera: degrees around (0 = from the front, -z) and up, and
    // the distance (default: far enough to fit everything in).
    yaw?: number;
    pitch?: number;
    distance?: number;
    // Blocks: press the green flag before the picture (with a settle time, so the project gets going).
    start?: boolean;
}

// What a controller reports after applying a view: the view as it now is, plus whatever helps choose the next one
// (a whiteboard's content bounds, a scene's bounding box), as plain JSON.
export type TabViewState = Record<string, unknown>;

export interface TabViewController {
    // Resolves once the tab has loaded and drawn everything it is going to.
    ready(): Promise<void>;
    // Applies the view (null: the tab's default) and resolves once it is drawn.
    apply(view: TabView | null): Promise<TabViewState>;
    // Holds the tab's animation while a picture is taken, and lets it go again. Tabs that don't animate have none.
    freeze?(): Promise<void>;
    thaw?(): Promise<void>;
}

const controllers = new Map<ProjectDataIndex, TabViewController>();
const waiters = new Map<ProjectDataIndex, ((controller: TabViewController) => void)[]>();

export function registerTabViewController(dataIndex: ProjectDataIndex, controller: TabViewController) {
    controllers.set(dataIndex, controller);
    waiters.get(dataIndex)?.forEach(resolve => resolve(controller));
    waiters.delete(dataIndex);

    return () => {
        if (controllers.get(dataIndex) === controller) {
            controllers.delete(dataIndex);
        }
    };
}

// The slot's controller, once one registers; null if none has within timeoutMs (the tab has no view of its own).
export function tabViewController(dataIndex: ProjectDataIndex, timeoutMs: number): Promise<TabViewController | null> {
    const existing = controllers.get(dataIndex);
    if (existing) {
        return Promise.resolve(existing);
    }

    return new Promise(resolve => {
        const timer = window.setTimeout(() => resolve(null), timeoutMs);
        const list = waiters.get(dataIndex) ?? [];
        list.push(controller => {
            window.clearTimeout(timer);
            resolve(controller);
        });
        waiters.set(dataIndex, list);
    });
}

/*
 * Registers the tab's controller, and applies the view the address bar names
 * once the tab has drawn, when the address bar's ?tab= is this tab. The
 * render page applies its views itself.
 */
export function useTabView(dataIndex: ProjectDataIndex, controller: TabViewController | null) {
    useEffect(() => (controller ? registerTabViewController(dataIndex, controller) : undefined), [dataIndex, controller]);

    const [params] = useSearchParams();
    const urlTab = params.get('tab') ?? 'project';
    const viewKey = tabViewToParams(tabViewFromParams(params) ?? {}).toString();

    useEffect(() => {
        const view = tabViewFromParams(new URLSearchParams(viewKey));
        const thisTab = dataIndex === ProjectDataIndex.Tab1 ? 'project' : `tab${dataIndex}`;
        if (!controller || !view || isRenderMode() || urlTab !== thisTab) {
            return;
        }

        let cancelled = false;
        void controller.ready().then(() => {
            if (!cancelled) {
                void controller.apply(view);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [controller, dataIndex, urlTab, viewKey]);
}

// --- The address bar --------------------------------------------------------------------------------------------

const numberParam = (params: URLSearchParams, name: string): number | undefined => {
    const value = params.get(name);
    if (value === null || value.trim() === '') {
        return undefined;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
};

const listParam = <T extends number[]>(params: URLSearchParams, name: string, length: number): T | undefined => {
    const parts = params.get(name)?.split(',').map(Number);
    return parts && parts.length === length && parts.every(Number.isFinite) ? (parts as T) : undefined;
};

export const tabViewParamNames = [
    'x', 'y', 'zoom', 'region', 'fit', 'cam', 'target', 'fov', 'yaw', 'pitch', 'distance', 'start'
];

// The view named in the address bar, or null when it names none.
export function tabViewFromParams(params: URLSearchParams): TabView | null {
    const view: TabView = {
        x: numberParam(params, 'x'),
        y: numberParam(params, 'y'),
        zoom: numberParam(params, 'zoom'),
        region: listParam(params, 'region', 4),
        fit: params.get('fit') === '1' ? true : undefined,
        camera: listParam(params, 'cam', 3),
        target: listParam(params, 'target', 3),
        fov: numberParam(params, 'fov'),
        yaw: numberParam(params, 'yaw'),
        pitch: numberParam(params, 'pitch'),
        distance: numberParam(params, 'distance'),
        start: params.get('start') === '1' ? true : undefined
    };

    const named = Object.fromEntries(Object.entries(view).filter(([, value]) => value !== undefined)) as TabView;
    return Object.keys(named).length > 0 ? named : null;
}

export function tabViewToParams(view: TabView): URLSearchParams {
    const params = new URLSearchParams();
    const set = (name: string, value: number | boolean | number[] | undefined) => {
        if (value === undefined) {
            return;
        }
        params.set(name, Array.isArray(value) ? value.map(round).join(',') : value === true ? '1' : String(round(value as number)));
    };

    set('x', view.x);
    set('y', view.y);
    set('zoom', view.zoom);
    set('region', view.region);
    set('fit', view.fit);
    set('cam', view.camera);
    set('target', view.target);
    set('fov', view.fov);
    set('yaw', view.yaw);
    set('pitch', view.pitch);
    set('distance', view.distance);
    set('start', view.start);
    return params;
}

const round = (value: number) => Math.round(value * 1000) / 1000;

// --- Render mode ------------------------------------------------------------------------------------------------

// The render page (/render/{project}/{tab}) the renderer's headless browser opens: no chrome, no live connection.
export const isRenderMode = () => window.location.pathname.startsWith('/render/');

// Waits until the images in an element (and the document's fonts) have loaded, up to timeoutMs.
export async function imagesLoaded(root: ParentNode, timeoutMs = 10_000): Promise<void> {
    const pending = Array.from(root.querySelectorAll('img'))
        .filter(img => !img.complete)
        .map(img => new Promise<void>(resolve => {
            img.addEventListener('load', () => resolve(), {once: true});
            img.addEventListener('error', () => resolve(), {once: true});
        }));

    await Promise.race([
        Promise.all([...pending, document.fonts?.ready.then(() => undefined)]),
        new Promise(resolve => window.setTimeout(resolve, timeoutMs))
    ]);
}

export const nextFrames = (count = 2) =>
    new Promise<void>(resolve => {
        const step = (left: number) => (left <= 0 ? resolve() : requestAnimationFrame(() => step(left - 1)));
        step(count);
    });
